import React, { useMemo, useState } from 'react';
import { AlertTriangle, CalendarClock, ChevronRight, Loader2, Plus } from 'lucide-react';
import type { SifaArbeitsbereich, SifaGbu } from '../../../services/sifa/gbuTypes';
import {
  GBU_FILTER,
  GBU_FILTER_LABEL,
  GbuListenFilter,
  STATUS_TON,
  bereichName,
  filtereGbus,
  pruefungUeberschritten,
  statusText,
  zaehleGbus,
  formatTag,
} from './gbuModulLogic';

interface Props {
  gbus: SifaGbu[];
  bereiche: SifaArbeitsbereich[];
  laden: boolean;
  heute: string;
  isReadOnly?: boolean;
  isMobile?: boolean;
  /** Laufender Vorgang (anlegen). */
  busy?: boolean;
  onNeu: () => void;
  onOeffnen: (g: SifaGbu) => void;
}

/**
 * Die Ablage der Gefaehrdungsbeurteilungen: Nr., Titel, Arbeitsbereich,
 * Status mit Fassung und „Prüfung bis" (rot, wenn ueberschritten). Am Handy
 * eine Karte je GBU, am PC eine Tabelle.
 */
export const GbuListe: React.FC<Props> = ({ gbus, bereiche, laden, heute, isReadOnly = false, isMobile = false, busy = false, onNeu, onOeffnen }) => {
  const [filter, setFilter] = useState<GbuListenFilter>('aktiv');
  const zaehler = useMemo(() => zaehleGbus(gbus, heute), [gbus, heute]);
  const sichtbar = useMemo(() => filtereGbus(gbus, filter, heute), [gbus, filter, heute]);

  const anzahl = (f: GbuListenFilter): number | null => {
    switch (f) {
      case 'aktiv': return zaehler.aktiv;
      case 'entwurf': return zaehler.entwurf;
      case 'in_freigabe': return zaehler.inFreigabe;
      case 'freigegeben': return zaehler.freigegeben;
      case 'faellig': return zaehler.faellig;
      case 'archiviert': return zaehler.archiviert;
      default: return null;
    }
  };

  const statusChip = (g: SifaGbu) => (
    <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-bold border rounded-full whitespace-nowrap ${STATUS_TON[g.status]}`}>
      {statusText(g)}
    </span>
  );

  const pruefung = (g: SifaGbu) => {
    if (!g.pruefenBis) return <span className="text-slate-400">—</span>;
    const rot = pruefungUeberschritten(g, heute);
    return (
      <span className={`inline-flex items-center gap-1 ${rot ? 'text-red-700 font-bold' : 'text-slate-600'}`}>
        {rot && <CalendarClock className="w-3.5 h-3.5" />}
        {formatTag(g.pruefenBis)}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Gefährdungsbeurteilungen</h2>
          <p className="text-xs text-slate-500">
            {gbus.length === 0 ? 'Noch keine Beurteilung angelegt' : `${zaehler.aktiv} aktiv${zaehler.archiviert ? ` · ${zaehler.archiviert} archiviert` : ''}`}
            {zaehler.faellig > 0 && <span className="text-red-600 font-bold"> · {zaehler.faellig} Prüfung überschritten</span>}
          </p>
        </div>
        <button
          onClick={onNeu}
          disabled={isReadOnly || busy}
          title={isReadOnly ? 'Nur-Ansicht: keine neue Gefährdungsbeurteilung' : undefined}
          className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-xl transition-colors disabled:opacity-40"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Neue GBU
        </button>
      </div>

      {gbus.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {GBU_FILTER.map((f) => {
            const n = anzahl(f);
            if (f !== 'aktiv' && f !== 'alle' && !n) return null;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`min-h-[36px] px-3 text-xs font-bold rounded-full border transition-colors ${filter === f
                  ? 'bg-slate-800 text-white border-slate-800'
                  : f === 'faellig' ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              >
                {GBU_FILTER_LABEL[f]}{n != null ? ` (${n})` : ''}
              </button>
            );
          })}
        </div>
      )}

      {laden ? (
        <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
      ) : gbus.length === 0 ? (
        <div className="p-8 bg-white border border-dashed border-slate-300 rounded-2xl text-center">
          <p className="text-sm text-slate-600 font-semibold">Noch keine Gefährdungsbeurteilung</p>
          <p className="text-xs text-slate-500 mt-1">
            Jede Beurteilung gehört zu einem Arbeitsbereich aus „Betrieb". „Neue GBU" legt einen Entwurf an;
            die KI schlägt Gefährdungen, Maßnahmen und Vorsorge vor, übernommen wird nur, was angehakt ist.
          </p>
        </div>
      ) : sichtbar.length === 0 ? (
        <div className="p-6 bg-white border border-slate-200 rounded-2xl text-center text-xs text-slate-500">
          Keine Beurteilung in „{GBU_FILTER_LABEL[filter]}".
        </div>
      ) : isMobile ? (
        <div className="grid grid-cols-1 gap-3">
          {sichtbar.map((g) => (
            <button key={g.id} onClick={() => onOeffnen(g)} className="text-left bg-white border border-slate-200 rounded-2xl p-4 space-y-2 hover:border-amber-300 transition-colors">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-bold text-slate-800 min-w-0">Nr. {g.nummer} — {g.titel || 'ohne Titel'}</p>
                <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
              </div>
              <p className="text-xs text-slate-500">{bereichName(bereiche, g.bereichId)}</p>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {statusChip(g)}
                <span className="text-slate-500">Prüfung bis {pruefung(g)}</span>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left w-14">Nr.</th>
                <th className="px-3 py-2 text-left">Titel</th>
                <th className="px-3 py-2 text-left">Arbeitsbereich</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Prüfung bis</th>
                <th className="px-3 py-2 w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sichtbar.map((g) => (
                <tr key={g.id} onClick={() => onOeffnen(g)} className="hover:bg-amber-50/40 cursor-pointer">
                  <td className="px-3 py-2.5 font-black text-slate-700">{g.nummer}</td>
                  <td className="px-3 py-2.5 font-semibold text-slate-800">{g.titel || 'ohne Titel'}</td>
                  <td className="px-3 py-2.5 text-slate-600">{bereichName(bereiche, g.bereichId)}</td>
                  <td className="px-3 py-2.5">{statusChip(g)}</td>
                  <td className="px-3 py-2.5 text-xs">{pruefung(g)}</td>
                  <td className="px-3 py-2.5 text-slate-400"><ChevronRight className="w-4 h-4" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {zaehler.faellig > 0 && filter !== 'faellig' && (
        <p className="text-xs text-red-700 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" /> {zaehler.faellig === 1 ? 'Bei einer Beurteilung' : `Bei ${zaehler.faellig} Beurteilungen`} ist das Prüfdatum überschritten — fortschreiben und erneut freigeben.
        </p>
      )}
    </div>
  );
};
